import { getSession } from './supabase/supabase-utils';
import invariant from 'tiny-invariant';
import { breadcrumb, log } from '@utils/logging';
import {
    EventMembership,
    InviteUserToEventBody,
    InviteUserToTeamBody,
    NewAthlete,
    TeamAthlete,
} from '@type/db-types';
import { API_URL } from './supabase/supabase-config';
import { toast } from 'react-toastify';
import { stringFromError } from '@utils/utils';
import {
    EventCheckoutBody,
    GetStripeInfoResponse,
    GetStripeSecretResponse,
    SignUp,
    StripeCheckoutSessionResponse,
} from '@type/api-types';
import Stripe from 'stripe';
import { GenerateLinkProperties } from '@supabase/supabase-js';
import { getUser } from './supabase/users';
import { isProduction } from './config';
import { SpoofUserForm } from '@components/SideBar/SpoofModal';

async function postToApi<T>(
    path: string,
    description: string,
    method = 'POST',
    body?: object | undefined,
): Promise<T> {
    const session = await getSession();
    invariant(session, `${description}: requires a signed in user`);

    breadcrumb(`posting: ${JSON.stringify(path)}`, { path, body });

    const response = await fetch(path, {
        method,
        headers: {
            Authorization: `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
        },
        body: body
            ? JSON.stringify({
                  ...body,
              })
            : undefined,
    });

    breadcrumb(`PostToApi ${description} Response`, {
        responseStatus: response.status,
        responseOk: response.ok,
        responseStatusText: response.statusText,
    });

    return handleResponse<T>(response);
}

async function handleResponse<T>(response: Response) {
    if (response?.status === 401) {
        console.log(response.status, response.url);
        throw new Error('Invalid token');
    }

    if (response?.status === 403) {
        breadcrumb('API call forbidden', { response });
        throw new Error('Unauthorized. User already exists.');
    }

    if (response?.status === 404) {
        console.log(response.status, response.url);
        breadcrumb('endpoint not found!', { response });
        throw new Error('Endpoint not found');
    }

    if (!response?.ok) {
        breadcrumb('bad response!', { response });
        // TODO: maybe defaulting to text() is a bad idea? Depends on our API protocol for 500s I think...
        throw new Error((await response.text()) || 'Bad response from api');
    }

    return response.json() as T;
}

export const updateUserEmail = async (
    userId: string,
    email: string,
): Promise<NewAthlete | null> => {
    breadcrumb('updateUserEmail', { userId, email });

    if (!userId || !email) {
        console.warn('not updating user email: missing userId or email');
        return null;
    }
    const existingUser = await getUser(userId);

    if (!existingUser) {
        console.warn('not updating user email: user not found');
        return null;
    } else if (existingUser.email === email) {
        console.warn('not updating user email: email is the same');
        return null;
    }

    // FIXME: discus flow/use-case with Dawson and re-enable this...
    if (isProduction) {
        alert(
            'Email editing is currently disabled, but will be enabled soon. We will be enabling this later in December, please speak to an admin if you need to update an email for now',
        );
        log(`User editing email: ${userId} -> ${email}`);
        return null;
    }

    return toast
        .promise(
            postToApi<NewAthlete>(`${API_URL}/user/${userId}/email`, 'updateUserEmail', 'POST', {
                email,
            }),
            {
                pending: 'Saving user email...',
                success: 'Email Saved!',
                error: 'Error saving email',
            },
        )
        .catch((error) => {
            log(`[updateUserEmail]: ${error.message}`);
            toast('Error editing email', { type: 'error' });
            return null;
        });
};
export const addChildToUser = async (invite: NewAthlete): Promise<NewAthlete | null> => {
    breadcrumb('addChildToUser', invite);

    return toast
        .promise(
            postToApi<NewAthlete>(
                `${API_URL}/user/add-child-athlete`,
                'addChildToUser',
                'POST',
                invite,
            ),
            {
                pending: 'Saving wrestler profile...',
                success: 'Profile Saved!',
                error: 'Error saving profile',
            },
        )
        .catch((error) => {
            log(`[addChildToUser]: ${error.message}`);
            toast('Error inviting user', { type: 'error' });
            return null;
        });
};
export const getSpoofUrl = async (invite: SpoofUserForm): Promise<string | null> => {
    const { email, firstName, lastName } = invite;
    breadcrumb('getSpoofUrl', email);

    return toast
        .promise(
            postToApi<GenerateLinkProperties>(
                `${API_URL}/spoof/${email}?firstName=${firstName}&lastName=${lastName}`,
                'getSpoofUrl',
                'GET',
            ).then((result) => {
                if (result?.action_link) navigator.clipboard.writeText(result?.action_link);
                return result?.action_link;
            }),
            {
                pending: 'Fetching spoof session...',
                success: 'URL Copied to clipboard!',
                error: 'Error spoofing user',
            },
        )
        .catch((error) => {
            log(`[addChildToUser]: ${error.message}`);
            toast('Error inviting user', { type: 'error' });
            return null;
        });
};

export const inviteUserToTeam = async (
    invite: InviteUserToTeamBody,
): Promise<TeamAthlete | null> => {
    breadcrumb('inviteUserToTeam', invite);

    return toast
        .promise(
            postToApi<TeamAthlete>(
                `${API_URL}/team/${invite.teamId}/invite`,
                'inviteUserToTeam',
                'POST',
                invite,
            ),
            invite?.email?.length
                ? {
                      pending: `Sending invite (${invite.firstName} ${invite.lastName})...`,
                      success: `Invite sent! (${invite.firstName} ${invite.lastName})`,
                      error: 'Error sending invite',
                  }
                : {
                      pending: `Adding user: (${invite.firstName} ${invite.lastName})...`,
                      success: `User added: (${invite.firstName} ${invite.lastName})`,
                      error: 'Error adding user',
                  },
        )
        .catch((error) => {
            log(`[inviteUserToTeam]: ${error.message}`);
            toast('Error inviting user', { type: 'error' });
            return null;
        });
};

export const inviteUserToEvent = async (invite: InviteUserToEventBody) => {
    breadcrumb('inviteUserToEvent', invite);

    return toast
        .promise(
            postToApi<EventMembership>(
                `${API_URL}/event/${invite?.eventId}/invite`,
                'inviteUserToEvent',
                'POST',
                invite,
            ),
            {
                pending: 'Sending invite...',
                success: 'Profile Saved!',
                error: 'Error saving profile',
            },
        )
        .catch((error) => {
            log(`[inviteUserToEvent]: ${error.message}`);
            toast('Error inviting user', { type: 'error' });
            return null;
        });
};

/**
 * Fetch and initialize the stripe info for the user
 * @param userId - the user id
 * @returns StripeInfo
 */
export const initializeStripeInfo = async (
    userId: string,
): Promise<GetStripeInfoResponse['accountInfo'] | undefined> => {
    return postToApi<GetStripeInfoResponse>(
        `${API_URL}/stripe/user/${userId}`, // optionally add the user id to refresh
        'initializeStripeInfo',
        'GET',
        undefined,
    )
        .then(({ accountInfo }) => accountInfo)
        .catch((e: unknown) => {
            log(`[initializeStripeInfo]: ${stringFromError(e)}`);
            return undefined;
        });
};

/**
 * Fetch client session secret for stripe connect
 * @returns StripeInfo
 */
export const fetchStripeSessionSecret = async (): Promise<string> => {
    return postToApi<GetStripeSecretResponse>(
        `${API_URL}/stripe/onboarding_session`,
        'fetchStripeSessionSecret',
        'GET',
        undefined,
    )
        .then(({ sessionSecret }) => sessionSecret)
        .catch((e: unknown) => {
            log(`[fetchStripeSessionSecret]: ${stringFromError(e)}`);
            throw e; // FIXME: trickle errors like this up to our UI
        });
};

/**
 * Fetch client session secret for Checkout
 * @returns StripeInfo
 */
export const fetchStripeCheckoutSecret = async (
    signUps: SignUp[],
): Promise<Stripe.Response<Stripe.Checkout.Session>> => {
    const eventId = signUps[0].eventId;
    invariant(eventId, '[fetchStripeCheckoutSecret] eventId required');
    const body: EventCheckoutBody = {
        signUps,
        returnUrl: `${window.location.origin}/events/${eventId}?tab=registration&stripeSession={CHECKOUT_SESSION_ID}`,
    };
    return postToApi<StripeCheckoutSessionResponse>(
        `${API_URL}/stripe/checkout/event`,
        'fetchStripeCheckoutSecret',
        'POST',
        body,
    )
        .then(({ checkoutSession }) => checkoutSession)
        .catch((e: unknown) => {
            log(`[fetchStripeCheckoutSecret]: ${stringFromError(e)}`);
            throw e; // FIXME: trickle errors like this up to our UI
        });
};
